import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { FormItemProps, SelectProps } from 'antd'
import { DefaultOptionType, SelectValue } from 'antd/lib/select'
import cl from 'classnames'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'

import { Select as SelectElem, Spinner } from 'Shared/atoms'
import { withTooltip } from 'Shared/HOCs/withTooltip'
import debounce from 'Shared/utils'
import { hasError } from 'Shared/utils/validation'

import { EMPTY_LABELVALUE } from 'Core/constants'
import { FieldNameRegexType } from 'Core/interfaces'
import {
	parseOptionWithGroupValue,
	parseSelectValue,
	valueToGroupValue
} from 'Core/utils/dataFormat'

import FormItem from './FormItem'
import { parseInputLabel } from './helpers'
import NotFoundContent from './NotFoundContent'

import styles from './input.module.scss'

export type Option =
	| LabelValue<Key>
	| NodeLabelValue<Key>
	| GroupedLabelValue<Key>
	| GroupedNodeLabelValue<Key>
const Select = withTooltip(SelectElem)

enum SortDirection {
	DESC = 'Descending',
	ASC = 'Ascending'
}

type Props<T, FieldsType extends FieldValues> = Omit<SelectProps<SelectValue>, 'onSearch'> &
	FormItemProps & {
		label?: ReactNode
		wrapperClassName?: string
		control: Control<FieldsType>
		fieldName: keyof FieldsType | FieldNameRegexType
		onSearch: (params: {
			pageSize?: number
			page?: number
			sortOrder?: SortDirection
			sortField?: string
			query?: string
		}) => Promise<T>
		formatFunc: (res: T) => Array<Option>
		minLengthToSearch?: number
		validate?: boolean
		initialOptions?: Option[]
		hideDropdownIcon?: boolean
		showTooltip?: boolean
		useFullOption?: boolean
		shouldClearOptionsOnFocus?: boolean
		onBlurSelect?: () => void
		showArrow?: boolean
		isReadOnly?: boolean
		onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void
		onChange?: (
			value: SelectValue,
			option?: DefaultOptionType
		) => void
	}

// T is type of onSearch response, please use formatFunc to format it to Option
function FormSearchSelect<T = Entity[], FieldsType extends FieldValues = {}>({
	label,
	extra,
	wrapperClassName,
	control,
	fieldName,
	onSearch,
	formatFunc,
	validate,
	minLengthToSearch = 3,
	initialOptions = [],
	hideDropdownIcon = true,
	showTooltip = false,
	useFullOption = false,
	onClear,
	shouldClearOptionsOnFocus = false,
	onBlurSelect,
	allowClear = true,
	showArrow = false,
	onChange,
	...props
}: Props<T, FieldsType>) {
	const fetchRef = useRef(0)
	const inputRef = useRef<HTMLInputElement | null>(null)
	const inputLabelRef = useRef<string | undefined>(undefined)
	const [fetching, setFetching] = useState(false)
	const [options, setOptions] = useState<Array<Option>>([])
	const [showNoContent, setShowNoContent] = useState(false)
	const [searchValue, setSearchValue] = useState<string | undefined>(undefined)

	const setInputLabelRef = (value: string | undefined) => {
		inputLabelRef.current = value
	}

	useEffect(() => {
		const shouldApplyDefaultOptions =
			!isEqual(options, initialOptions) &&
			isEmpty(options) &&
			!isEmpty(initialOptions) &&
			!searchValue
		if (shouldApplyDefaultOptions) {
			setOptions(initialOptions)
		}
	}, [initialOptions, options, searchValue])

	const debouncedGetSearchOptions = useMemo(() => {
		const loadOptions = async (value: string) => {
			if (value.length < minLengthToSearch) {
				fetchRef.current = 0
				setOptions([])
				setShowNoContent(false)
				return
			}
			const fetchId = fetchRef.current
			setOptions([])
			setShowNoContent(true)
			setFetching(true)

			const newOptions = await onSearch({ query: value })

			if (fetchId !== fetchRef.current || fetchRef.current === 0) {
				// for fetch callback order
				return
			}

			const formattedOptions = formatFunc(newOptions!)

			setOptions(formattedOptions)
			setFetching(false)
		}

		return debounce(loadOptions, 300)
	}, [minLengthToSearch, onSearch, formatFunc])

	const onSearchHandler = (searchText: string) => {
		const searchTextTrim = searchText.trim()

		setSearchValue(searchText)

		fetchRef.current += 1

		debouncedGetSearchOptions(searchTextTrim)
	}

	const handleInputKeyDown = (event: React.KeyboardEvent) => {
		if (event.key !== 'Enter') {
			return
		}

		inputRef.current?.blur()
	}

	const onClearHandler = useCallback(() => {
		setInputLabelRef(undefined)
		setSearchValue(undefined)
		setShowNoContent(false)
		setOptions([])
		onClear?.()
	}, [onClear])

	const onChangeHandler = useCallback(
		(value: SelectValue, option?: DefaultOptionType) => {
			if (onChange) {
				onChange(value, option)
			}
		},
		[onChange]
	)

	const onFocus = (value: SelectValue | null) => {
		setSearchValue(inputLabelRef.current)

		if (shouldClearOptionsOnFocus && !value) {
			setShowNoContent(false)
			setOptions([])
		}
	}

	const getIsGrouped = (o: Option): o is GroupedLabelValue =>
		(o as GroupedLabelValue).options !== undefined

	const filteredOptions = options.reduce((acc, option) => {
		const isGroupedOption = getIsGrouped(option)
		const formattedOption = isGroupedOption
			? {
					label: option.label,
					options: (option as GroupedLabelValue).options!.map((item) => ({
						...item,
						label: item.label,
						value: valueToGroupValue(item.value, option.label)
					}))
			  }
			: option

		return !isGroupedOption || option.options.length ? [...acc, formattedOption] : acc
	}, [] as Option[])

	return (
		<Controller
			name={fieldName as FieldPath<FieldsType>}
			control={control}
			render={({ field: { onChange, name, value, ...rest }, fieldState: { error } }) => {
				// need for placeholder with labelInValue
				const correctValue =
					!props.labelInValue || (value as LabelValue)?.value !== '' ? value : null

				if (!correctValue && inputLabelRef.current) {
					setInputLabelRef(undefined)
				}

				const handleOnChange = (
					value: SelectValue,
					option: DefaultOptionType | DefaultOptionType[] | undefined
				) => {
					inputRef.current?.blur()
					const parsedValue = parseSelectValue(value)
					const parsedOption = parseOptionWithGroupValue(option)

					setInputLabelRef(parseInputLabel(parsedOption))

					if (shouldUseFullOption) {
						const formattedOption = isNil(parsedOption)
							? EMPTY_LABELVALUE
							: parsedOption
						onChangeHandler(formattedOption)
						onChange(formattedOption)
						return
					}

					onChangeHandler(parsedValue, parsedOption)
					onChange(parsedValue)
				}

				const shouldUseFullOption = props.labelInValue && useFullOption

				const handleRef = (input: HTMLInputElement) => {
					inputRef.current = input
				}

				return (
					<FormItem
						className={cl(styles.inputWrapper, styles.selectSearch, wrapperClassName)}
						label={label}
						error={hasError(error)}
						colon={false}
						extra={extra}
						name={name}
						validate={validate}
					>
						<Select
							labelInValue
							{...props}
							{...rest}
							searchValue={searchValue}
							value={correctValue}
							onSearch={onSearchHandler}
							showSearch
							dataTestId={`qaa-${name}`}
							showArrow={showArrow}
							filterOption={false}
							options={filteredOptions}
							allowClear={allowClear}
							onInputKeyDown={handleInputKeyDown}
							{...(hideDropdownIcon && {
								onClear: onClearHandler,
								notFoundContent: showNoContent ? (
									fetching ? (
										<Spinner />
									) : (
										<NotFoundContent isSearch />
									)
								) : null,
								suffixIcon: null
							})}
							{...(!hideDropdownIcon && {
								notFoundContent: fetching ? (
									<Spinner />
								) : (
									<NotFoundContent isSearch />
								)
							})}
							onChange={handleOnChange}
							showTooltip={!value && showTooltip}
							onFocus={() => onFocus(correctValue)}
							{...(onBlurSelect ? { onBlur: onBlurSelect } : {})}
							ref={handleRef}
						/>
					</FormItem>
				)
			}}
		/>
	)
}

export default FormSearchSelect
